export interface FormFieldMapping {
  id?: string
  formFieldName: string
  formFieldLabel: string
  formFieldType: 'text' | 'number' | 'email' | 'select' | 'textarea' | 'date' | 'phone' | 'currency' | 'percentage' | 'url' | 'boolean'
  formFieldCategory: string
  salesforceObject: 'Account' | 'Opportunity' | 'Contact'
  salesforceField: string
  isRequired: boolean
  displayOrder: number
  placeholder?: string
  helpText?: string
  validationRules?: any
  isActive: boolean
  // Enhanced Salesforce metadata fields
  salesforceFieldType?: string
  salesforceFieldLabel?: string
  salesforcePicklistOptions?: Array<{ label: string; value: string }>
  salesforceMaxLength?: number
  fieldMappingValidated?: boolean
  lastValidatedAt?: string
}

export interface GroupedFormFields {
  [category: string]: FormFieldMapping[]
}

class SubmitDealFieldMappingService {

  /**
   * Get form schema for ISO submit deal application
   */
  async getFormSchema(): Promise<GroupedFormFields> {
    try {
      const response = await fetch('/api/iso-submit-deal/form-schema', {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        throw new Error(`Failed to fetch form schema: ${response.statusText}`)
      }

      const data = await response.json()
      return data
    } catch (error) {
      console.error('Error fetching form schema:', error)
      throw error
    }
  }

  /**
   * Map form data to Salesforce objects using backend service
   */
  async mapFormDataToSalesforce(formData: { [key: string]: any }): Promise<{
    Account: { [key: string]: any }
    Opportunity: { [key: string]: any }
    Contact: { [key: string]: any }
  }> {
    try {
      const response = await fetch('/api/iso-submit-deal/map-data', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      })

      if (!response.ok) {
        throw new Error(`Failed to map form data: ${response.statusText}`)
      }

      const data = await response.json()
      return data
    } catch (error) {
      console.error('Error mapping form data:', error)
      throw error
    }
  }

  /**
   * Validate required fields before submission
   */
  validateRequiredFields(formData: { [key: string]: any }, fieldMappings: FormFieldMapping[]): {
    valid: boolean
    missingFields: string[]
  } {
    const missingFields: string[] = []

    for (const mapping of fieldMappings) {
      if (mapping.isRequired) {
        const value = formData[mapping.formFieldName]
        if (value === undefined || value === null || value === '') {
          missingFields.push(mapping.formFieldLabel)
        }
      }
    }

    return {
      valid: missingFields.length === 0,
      missingFields
    }
  }

  /**
   * Get categories in proper display order
   */
  getCategoriesInOrder(groupedFields: GroupedFormFields): string[] {
    const categoryOrder = [
      'Business',
      'Business Address', 
      'Primary Contact',
      'Contact Address',
      'Deal Information'
      // Removed 'General' - no longer needed
    ]

    const availableCategories = Object.keys(groupedFields)
      .filter(cat => cat !== 'General') // Filter out General category
    
    // Return categories in preferred order, followed by any additional categories
    const orderedCategories = categoryOrder.filter(cat => availableCategories.includes(cat))
    const additionalCategories = availableCategories.filter(cat => !categoryOrder.includes(cat))
    
    return [...orderedCategories, ...additionalCategories]
  }

  /**
   * Sort fields within a category by display order
   */
  sortFieldsByOrder(fields: FormFieldMapping[]): FormFieldMapping[] {
    return fields.sort((a, b) => {
      if (a.displayOrder !== b.displayOrder) {
        return a.displayOrder - b.displayOrder
      }
      // If same display order, sort alphabetically by label
      return a.formFieldLabel.localeCompare(b.formFieldLabel)
    })
  }
}

export const submitDealFieldMappingService = new SubmitDealFieldMappingService()
